import { Heart, Phone, MapPin, Zap, CheckCircle, Clock, AlertCircle, IndianRupee, Mail, Target, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const AboutUs = () => {
  const { hash } = useLocation();

  // Scroll to top or hash when component mounts or hash changes
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  const heroImages = [
    "/Screenshot 2026-03-16 191914.png",
    "/Screenshot 2026-03-16 191954.png",
    "/Screenshot 2026-03-16 192022.png",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const fadeInUp: any = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const staggerContainer: any = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const scaleIn: any = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const problems = [
    {
      icon: Clock,
      title: "Long Waiting Times",
      description: "It can take days, sometimes weeks, to get a single answer sheet reviewed.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: AlertCircle,
      title: "Inconsistent Feedback",
      description: "The quality of feedback can vary dramatically from one evaluator to another.",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: IndianRupee,
      title: "High Costs",
      description: "Access to premium, one-on-one mentorship and evaluation is often prohibitively expensive.",
      color: "from-red-500 to-pink-500"
    }
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section style={{ backgroundImage: "url('/herobg.webp')" }} className="relative py-32 h-screen flex justify-center items-center bg-gradient-to-br from-purple-700 to-indigo-700 m-2 p-4 md:m-4 mt-0 rounded-2xl overflow-hidden">


        <motion.div
          className="relative z-10 container max-w-5xl mx-auto text-center space-y-8"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="inline-flex  items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-white text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              <span>Our Mission</span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
            About <span className="text-white">Parakh</span>
          </motion.h1>

          {/* Description */}
          <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-white max-w-3xl mx-auto font-light leading-relaxed">
            A team of educators, technologists, and former aspirants fixing the biggest gap in Civil Services preparation: timely, consistent, and actionable feedback.
          </motion.p>

          {/* <motion.div variants={fadeInUp} className="pt-6">
            <p className="text-base text-white max-w-2xl mx-auto leading-relaxed">
              A team of educators, technologists, and former aspirants fixing the biggest gap in Civil Services preparation: timely, consistent, and actionable feedback.
            </p>
          </motion.div> */}

          {/* Action */}
          <motion.div variants={fadeInUp} className="pt-8">
            <a
              href="#contact"
            >
              <Button className="inline-flex items-center gap-2 h-14 px-8 bg-white text-indigo-950 font-bold rounded-full shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all duration-300">

                Get in Touch
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* The Problem Section */}
      <section className="container py-24">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-20 max-w-3xl mx-auto">
            <span className="text-sm font-bold text-red-500 uppercase tracking-widest pl-1">
              The Challenge
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1] mt-6 mb-8">
              Why preparation feels <span className="text-indigo-600">broken.</span>
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed font-light">
              Every year, lakhs of aspirants spend countless hours writing,
              yet the feedback loop remains the biggest bottleneck in their success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="group p-10 h-full rounded-[32px] bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-2xl transition-all duration-500">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${problem.color} flex items-center justify-center mb-8 shadow-lg transform group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300`}>
                    <problem.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{problem.title}</h3>
                  <p className="text-gray-500 leading-relaxed font-medium">{problem.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Our Solution Section (Bento Grid) */}
      <section className="container p-2 md:py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end items-center justify-between gap-8 mb-16">
            <motion.h2 variants={fadeInUp} className="text-5xl text-center md:text-left lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1] max-w-2xl">
              Delivering
              <span className="text-indigo-600"> AI Precision</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-center md:text-left lg:text-right text-gray-500 max-w-md lg:max-w-lg font-light leading-relaxed lg:mb-2"
            >
              We are a team of educators and technologists passionate about making UPSC evaluation accessible and accurate.
            </motion.p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

            {/* Card 1: Large Feature (Left) */}
            <motion.div variants={scaleIn} className="lg:col-span-5 relative h-[500px] rounded-[40px] bg-[#0f1115] overflow-hidden p-10 flex flex-col justify-between group">
              <div className="relative z-10">
                <span className="text-amber-400 font-bold tracking-widest text-xs uppercase mb-4 block">Ready for Mains</span>
                <h3 className="text-4xl font-bold text-white leading-tight">
                  Leveraging AI to drive better <br /> answer writing.
                </h3>
              </div>

              <div className="relative z-10">
                <Link to={"/evaluate"} className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-full group-hover:bg-indigo-50 transition-colors cursor-pointer">
                  Start Evaluating <Sparkles className="w-4 h-4 ml-1" />
                </Link>
              </div>


              {/* AI Scanner Visualizarion */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-40 group-hover:opacity-60 transition-opacity duration-700">
                <div className="relative w-full h-full p-12">
                  <div className="relative w-full h-full rounded-2xl overflow-hidden border border-white/20 bg-white/5 backdrop-blur-sm shadow-2xl">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentHeroImage}
                        src={heroImages[currentHeroImage]}
                        className="w-full h-full object-cover"
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.8 }}
                      />
                    </AnimatePresence>

                    {/* Scanning Beam */}
                    <motion.div
                      className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-400 to-transparent shadow-[0_0_20px_rgba(129,140,248,0.8)] z-10"
                      initial={{ top: "-5%" }}
                      animate={{ top: ["0%", "100%", "0%"] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    />

                    {/* Grid Overlay */}
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" />
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(129,140,248,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(129,140,248,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

                    {/* Corner Markers */}
                    <div className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-indigo-400/50" />
                    <div className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-indigo-400/50" />
                    <div className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-indigo-400/50" />
                    <div className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-indigo-400/50" />

                    {/* Data Points */}
                    <motion.div
                      className="absolute top-1/4 left-1/3 w-2 h-2 rounded-full bg-indigo-400 shadow-[0_0_10px_rgba(129,140,248,1)]"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <motion.div
                      className="absolute top-2/3 right-1/4 w-2 h-2 rounded-full bg-purple-400 shadow-[0_0_10px_rgba(192,132,252,1)]"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    />
                  </div>
                </div>
              </div>

              {/* Enhanced Purple Glow (Original but moved behind content) */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-blue-600/30 to-indigo-600/30 rounded-full blur-[100px] pointer-events-none z-0" />
            </motion.div>

            {/* Middle Column: Stacked Cards */}
            <div className="lg:col-span-4 flex flex-col gap-6 h-[500px]">
              {/* Card 2: Parameters */}
              <motion.div variants={fadeInUp} className="flex-1 rounded-[40px] bg-gray-100 p-8 flex flex-col justify-between transition-all duration-300 group cursor-pointer">
                <div>
                  <p className="text-gray-600 text-sm mb-2">Comprehensive Analysis</p>
                  <h3 className="text-3xl font-bold text-indigo-950">15+ Parameters</h3>

                  <p className="mt-2 text-gray-500 text-sm leading-relaxed">
                    Detailed analysis of structure, flow, and content relevance for every answer.
                  </p>
                </div>
                <div className="self-end p-3 rounded-full bg-white group-hover:bg-indigo-600 group-hover:text-white transition-all">
                  <Zap className="w-5 h-5" />
                </div>
              </motion.div>

              {/* Card 3: Instant Results */}
              <motion.div variants={fadeInUp} className="flex-1 rounded-[40px] bg-gray-100 p-8 flex flex-col justify-between transition-all duration-300 group cursor-pointer">
                <div>
                  <p className="text-gray-600 text-sm mb-2">Zero Waiting Time</p>
                  <h3 className="text-3xl font-bold text-rose-950">Instant Results</h3>
                  <p className="mt-2 text-gray-500 text-sm leading-relaxed">Get instant feedback on your answers within seconds, not days.</p>
                </div>
                <div className="self-end p-3 rounded-full bg-white group-hover:bg-rose-600 group-hover:text-white transition-all">
                  <Clock className="w-5 h-5" />
                </div>
              </motion.div>
            </div>

            {/* Right Column: Tall Card */}
            <motion.div variants={fadeInUp} className="lg:col-span-3 h-[500px] rounded-[40px] bg-gray-100 p-8 flex flex-col justify-between  transition-all duration-300 group cursor-pointer">
              <div>
                <p className="text-gray-600 text-sm mb-4">Trained on Toppers</p>
                <h3 className="text-3xl font-bold text-gray-900 leading-tight">
                  Expert Vetted Model
                </h3>
                <p className="mt-4 text-gray-500 text-sm leading-relaxed">
                  Our AI is fine-tuned on 2000+ evaluated copies from successful candidates to ensure alignment with UPSC standards.
                </p>
              </div>
              <div className="self-end p-3 rounded-full bg-white group-hover:bg-black group-hover:text-white transition-all">
                <Target className="w-5 h-5" />
              </div>
            </motion.div>

          </div>
        </motion.div>
      </section>

      {/* Why Choose Parakh */}
      {/* Why Choose Parakh (Showcase Style) */}
      <section className="container py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1] mb-6">
              Why toppers choose <br />
              <span className="text-indigo-600">Parakh.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">

            {/* Card 1: Affordable / Community */}
            <motion.div variants={fadeInUp} className="relative overflow-hidden rounded-[40px] bg-gray-50 hover:bg-white transition-all duration-300 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 pt-12 flex flex-col justify-between min-h-[480px] group hover:shadow-xl transition-all duration-300">
              <div className="text-center mx-auto max-w-xs relative z-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Accessible to All</h3>
                <p className="text-gray-500 font-medium leading-relaxed">
                  Quality evaluation shouldn't be a luxury. We make expert feedback affordable for every aspirant.
                </p>
              </div>

              {/* Visual: Avatar Grid */}
              <div className="relative mt-10 mx-auto w-full max-w-[280px]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded-2xl shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">A</div>
                    <div className="h-2 w-12 bg-gray-100 rounded-full" />
                  </div>
                  <div className="bg-white p-3 rounded-2xl shadow-sm flex items-center gap-3 translate-y-4">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-xs">S</div>
                    <div className="h-2 w-12 bg-gray-100 rounded-full" />
                  </div>
                  <div className="bg-white p-3 rounded-2xl shadow-sm flex items-center gap-3 -translate-y-2">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-xs">R</div>
                    <div className="h-2 w-12 bg-gray-100 rounded-full" />
                  </div>
                  <div className="bg-white p-3 rounded-2xl shadow-sm flex items-center gap-3 translate-y-2">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xs">P</div>
                    <div className="h-2 w-12 bg-gray-100 rounded-full" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2: Fast / Exact Reports */}
            <motion.div variants={fadeInUp} className="relative overflow-hidden rounded-[40px] p-8 bg-gray-50 hover:bg-white transition-all duration-300 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]  pt-12 flex flex-col justify-between min-h-[480px] group hover:shadow-xl transition-all duration-300">
              <div className="text-center mx-auto max-w-xs relative z-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Instant Reports</h3>
                <p className="text-gray-500 font-medium leading-relaxed">
                  Get detailed scoring and feedback within seconds of submission. No more waiting.
                </p>
              </div>

              {/* Visual: Score Card */}
              <div className="relative mt-12 mx-auto w-full max-w-[260px]">
                <div className="bg-white p-6 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] transform group-hover:-translate-y-2 transition-transform duration-500">
                  <div className="flex justify-between items-end mb-6">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total Score</p>
                      <h4 className="text-3xl font-bold text-gray-900">115<span className="text-lg text-gray-400">/250</span></h4>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full w-[70%] bg-indigo-500 rounded-full" />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 font-medium">
                      <span>Analysis Complete</span>
                      <span>1.2s</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 3: Accurate / User Stats */}
            <motion.div variants={fadeInUp} className="relative overflow-hidden rounded-[40px] bg-gray-50 hover:bg-white transition-all duration-300 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 pt-12 flex flex-col justify-between min-h-[480px] group hover:shadow-xl transition-all duration-300">
              <div className="text-center mx-auto max-w-xs relative z-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Precision Scoring</h3>
                <p className="text-gray-500 font-medium leading-relaxed">
                  Our evaluation breaks down your answer into micro-parameters for granular insights.
                </p>
              </div>

              {/* Visual: List Items */}
              <div className="relative mt-10 mx-auto w-full max-w-[260px] space-y-3">
                {[
                  { label: "Structure", score: "8.5", color: "bg-orange-100 text-orange-700" },
                  { label: "Content", score: "7.0", color: "bg-blue-100 text-blue-700" },
                  { label: "Relevance", score: "9.0", color: "bg-purple-100 text-purple-700" },
                ].map((item, i) => (
                  <div key={i} className="bg-white p-3 px-4 rounded-xl shadow-sm flex items-center justify-between transform transition-transform" style={{ transitionDelay: `${i * 100}ms` }}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${item.color} flex items-center justify-center font-bold text-xs`}>
                        {item.label[0]}
                      </div>
                      <span className="text-sm font-bold text-gray-700">{item.label}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{item.score}</span>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </motion.div>
      </section>

      {/* Our Commitment */}
      {/* Our Commitment */}
      <section className="container py-32">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-16 md:text-center max-w-3xl mx-auto">
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1] mb-6">
              More than just <span className="text-indigo-600">code.</span>
            </h2>
            <p className="text-xl text-gray-900 font-light">
              We're building the future of preparation. Here is what drives us every single day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Unbiased Accuracy",
                desc: "No moods, no fatigue. Just pure, objective evaluation based on strict UPSC rubrics.",
                icon: Target,
                gradient: "from-blue-500 to-indigo-500"
              },
              {
                title: "Dedicated Partnership",
                desc: "We identify your specific weaknesses and help you turn them into your strongest assets.",
                icon: Heart,
                gradient: "from-rose-500 to-pink-500"
              },
              {
                title: "Selection Focus",
                desc: "We don't stop at feedback. We stop when you walk into the examination hall with total confidence.",
                icon: Sparkles,
                gradient: "from-amber-400 to-orange-500"
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="group relative h-full min-h-[300px] p-8 rounded-[40px] bg-gray-50 hover:bg-white border border-gray-200 overflow-hidden transition-all duration-500 hover:-translate-y-2"
              >

                <div className="relative z-10 flex flex-col h-full justify-between">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg mb-6 group-hover:scale-110 transition-transform duration-500`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">{item.title}</h3>
                    <p className="text-gray-900 leading-relaxed text-sm md:text-base group-hover:text-gray-900 transition-colors">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Contact Us */}
      <section id="contact" className="container py-24 scroll-mt-20">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              Get in Touch
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              We'd love to <span className="text-indigo-600">hear from you.</span>
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: MapPin, title: "Visit Us", content: "Kuberya-AI Office, Vijay Nagar, SBI Chowk, Jabalpur, Madhya Pradesh, India", sub: "India" },
                { icon: Phone, title: "Call Us", content: "+91 9009022958", sub: "Mon-Sat 9am-6pm" },
                { icon: Mail, title: "Email Us", content: "kuberyaai@gmail.com", sub: "We reply within 24h" }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="p-8 rounded-[32px] bg-gray-50 border border-transparent hover:bg-white hover:border-gray-100 hover:shadow-xl transition-all duration-300 group text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 font-medium">{item.content}</p>
                  <p className="text-gray-400 text-sm mt-1">{item.sub}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutUs;
